import StarInput from "~/ui/StarInput";
import Heading from "~/ui/Heading";
import type { LoaderData } from "~/routes/companies.$slug._index/route";
import LinkPaginationControls from "~/ui/LinkPaginationControls";
import List, { ListItem } from "~/ui/List";
import deserializeReview from "~/routes/companies.$slug._index/deserializeReview";
import describeTimeSince from "~/kernel/dateTime/describeTimeSince";
import LinkButton from "~/ui/LinkButton";
import ROUTES from "~/kernel/routing/ROUTES";
import { useLoaderData } from "@remix-run/react";
import Link from "~/ui/Link";
import REVIEWS_SECTION_ID from "./REVIEWS_SECTION_ID";
import useTranslation from "~/kernel/internationalization/useTranslation";
import { ReviewAuthorBadge, ReviewThread } from "~/review/shared";

export function scrollToReviews() {
  const element = document.getElementById(REVIEWS_SECTION_ID);

  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
}

export default function ReviewsSection() {
  const { company } = useLoaderData<LoaderData>();
  const { tt } = useTranslation("reviewsSection");

  return (
    <div className="space-y-8" id={REVIEWS_SECTION_ID}>
      <div className="space-y-5">
        <Heading level={2}>{tt("heading")}</Heading>
        <p>
          {tt("description", {
            companyName: company.name,
            interpolation: { escapeValue: false },
          })}
        </p>

        <LinkButton
          to={ROUTES.newReview.path({ params: { slug: company.slug } })}
          variant="secondary"
        >
          {tt("leaveReview")}
        </LinkButton>
      </div>
      <hr className="border-b-slate-200" />
      <Reviews />
    </div>
  );
}

function Reviews() {
  const { reviews: reviewsResult } = useLoaderData<LoaderData>();
  const { t, tt } = useTranslation("reviewsSection");

  if (reviewsResult.totalCount < 1) {
    return <p>{tt("noReviews")}</p>;
  }

  const reviews = reviewsResult.data.map(deserializeReview);
  return (
    <div className="space-y-5">
      <LinkPaginationControls result={reviewsResult} />
      <List>
        {reviews.map((review) => (
          <ListItem key={review.id}>
            <div className="space-y-3">
              <div>
                <div className="flex flex-row gap-2 items-center">
                  <span className="font-bold">
                    {review?.reviewerName ?? tt("anonymous")}
                  </span>
                  {review.author ? (
                    <ReviewAuthorBadge author={review.author} />
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <StarInput
                    size="small"
                    defaultValue={review.score}
                    readOnly
                  />
                  <span className="text-slate-400">
                    {describeTimeSince(review.createdAt, { t })}
                  </span>
                </div>
              </div>
              <div>
                <p>{review.reviewText}</p>
              </div>
              {review.comments.length > 0 ? (
                <div className="pl-5">
                  <ReviewThread comments={review.comments} />
                  <ReviewToolbar
                    reviewId={review.id}
                    canRespond={review.canRespond}
                    bookingId={review.bookingId}
                  />
                </div>
              ) : (
                <ReviewToolbar
                  reviewId={review.id}
                  canRespond={review.canRespond}
                  bookingId={review.bookingId}
                />
              )}
            </div>
          </ListItem>
        ))}
      </List>
    </div>
  );
}

type ReviewToolbarProps = {
  reviewId: string;
  canRespond: boolean;
  bookingId: string | null;
};

function ReviewToolbar({
  reviewId,
  canRespond,
  bookingId,
}: ReviewToolbarProps) {
  const { company } = useLoaderData<LoaderData>();
  const { tt } = useTranslation("reviewsSection");

  if (!canRespond) {
    return null;
  }

  return (
    <div className="flex flex-row gap-3 items-center">
      <Link
        to={ROUTES.reviewThread.path({
          params: { slug: company.slug, reviewId },
        })}
      >
        {tt("respond")}
      </Link>
      {bookingId ? (
        <Link to={ROUTES.companyEditBooking.path({ id: bookingId })}>
          {tt("showBooking")}
        </Link>
      ) : null}
    </div>
  );
}
